import { relations } from 'drizzle-orm'
import { boolean, index, integer, pgTable, text, timestamp, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'

import { user } from '../../auth'
import { Book } from '../../content'
import { Premium } from '../../membership'
import { LearnProgress } from './learn-progress'
import { PageReadingActivity } from './page-reading-activity'
import { ReadingSessionSummary } from './reading-session-summary'

// =============================================================================
// READING SESSION - Lightweight session header (immutable after creation)
// =============================================================================

export const ReadingSession = pgTable(
  'reading_session',
  {
    id: uuid().primaryKey().defaultRandom(),
    bookId: uuid()
      .references(() => Book.id, { onDelete: 'restrict' })
      .notNull(),
    learnProgressId: uuid().references(() => LearnProgress.id, { onDelete: 'set null' }),
    userId: text().references(() => user.id, { onDelete: 'set null' }),
    premiumId: uuid().references(() => Premium.id, { onDelete: 'restrict' }),
    startedAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),

    // DEAD FLAGS — the next four pairs fed the deleted per-table producers. Nothing reads them: the
    // daily lookup tables are recomputed from the sessions by `compute<Metric>`
    // (packages/trigger/src/metrics/), and "Lanjut Baca" is read live. Kept until a migration drops them.

    // Reading journal tracking
    calculatedForReadingJournal: boolean().default(false).notNull(),
    readingJournalCalculatedAt: timestamp({ mode: 'date', withTimezone: true }),

    // Reading metrics daily tracking
    calculatedForReadingMetricsDaily: boolean().default(false).notNull(),
    readingMetricsDailyCalculatedAt: timestamp({ mode: 'date', withTimezone: true }),

    // Reading history daily tracking
    calculatedForReadingHistoryDaily: boolean().default(false).notNull(),
    readingHistoryDailyCalculatedAt: timestamp({ mode: 'date', withTimezone: true }),

    // Continue reading tracking
    calculatedForContinueReading: boolean().default(false).notNull(),
    continueReadingCalculatedAt: timestamp({ mode: 'date', withTimezone: true }),

    // LIVE FLAGS — one-way ledgers for the royalty and book-metrics crons. Never reset these.

    // Royalty tracking
    calculatedForRoyalty: boolean().default(false).notNull(),
    royaltyCalculatedAt: timestamp({ mode: 'date', withTimezone: true }),

    // Book metrics tracking (all-time)
    calculatedForBookMetrics: boolean().default(false).notNull(),
    bookMetricsCalculatedAt: timestamp({ mode: 'date', withTimezone: true }),

    // Book metrics monthly tracking (separate flag so the monthly cron consumes rows independently of all-time)
    calculatedForBookMetricsMonthly: boolean().default(false).notNull(),
    bookMetricsMonthlyCalculatedAt: timestamp({ mode: 'date', withTimezone: true }),

    // LEGACY
    legacyReadHistoryId: integer(),
  },
  (t) => [
    index('idx_reading_session_user').on(t.userId),
    index('idx_reading_session_user_book').on(t.userId, t.bookId),
    index('idx_reading_session_book_metrics_monthly').on(t.calculatedForBookMetricsMonthly),
  ],
)

export const readingSessionRelations = relations(ReadingSession, ({ one, many }) => ({
  book: one(Book, {
    fields: [ReadingSession.bookId],
    references: [Book.id],
  }),

  premium: one(Premium, {
    fields: [ReadingSession.premiumId],
    references: [Premium.id],
  }),

  learnProgress: one(LearnProgress, {
    fields: [ReadingSession.learnProgressId],
    references: [LearnProgress.id],
  }),

  user: one(user, {
    fields: [ReadingSession.userId],
    references: [user.id],
  }),

  pageReadingActivities: many(PageReadingActivity),
  summary: one(ReadingSessionSummary),
}))

export const InsertReadingSessionSchema = createInsertSchema(ReadingSession).omit({
  id: true,
  createdAt: true,
})

export const SelectReadingSessionSchema = createSelectSchema(ReadingSession)

export type InsertReadingSession = typeof ReadingSession.$inferInsert
export type SelectReadingSession = typeof ReadingSession.$inferSelect
